import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelectionDropdown } from 'shared/components/dropdown/MultiSelectionDropdown';
import { useTeamSites } from 'dashboard/hooks/useTeamSites';
import { usePosthogCapture } from 'dashboard/posthogCapture/_index';
import * as Styled from 'dashboard/styles/Filters.style';

const ALL_SITES_VALUE = 'all';

interface SiteFilterProps {
  siteIds: string[];
  onSiteIdsChange: (ids: string[]) => void;
}

export default function SiteFilter({
  siteIds,
  onSiteIdsChange,
}: SiteFilterProps): JSX.Element {
  const { t } = useTranslation('pageDashboard');
  const { data: sites = [] } = useTeamSites();
  const dashboard = usePosthogCapture();

  const isAllSelected = siteIds.length === 0;

  const options = useMemo(
    () => [
      { value: ALL_SITES_VALUE, label: t('filters.allSites') },
      ...sites.map((site) => ({ value: site.id, label: site.name })),
    ],
    [sites, t]
  );

  const dropdownValue = useMemo(
    () => (isAllSelected ? [ALL_SITES_VALUE] : siteIds),
    [isAllSelected, siteIds]
  );

  const handleChange = useCallback(
    (newValues: string[]) => {
      const allNowInValues = newValues.includes(ALL_SITES_VALUE);
      const individualValues = newValues.filter((v) => v !== ALL_SITES_VALUE);

      let nextIds: string[];
      if (isAllSelected) {
        nextIds = individualValues.length > 0 ? individualValues : [];
      } else if (allNowInValues) {
        nextIds = [];
      } else {
        nextIds = individualValues;
      }

      onSiteIdsChange(nextIds);
      dashboard.siteFilterChanged({
        selection_type: nextIds.length === 0 ? 'all' : 'specific',
        site_count: nextIds.length,
      });
    },
    [isAllSelected, onSiteIdsChange, dashboard]
  );

  const renderValue = useCallback(
    (selectedLabels: string[]) => {
      if (isAllSelected) return t('filters.allSites');
      return selectedLabels.join(', ');
    },
    [isAllSelected, t]
  );

  return (
    <Styled.FilterWrapper>
      <MultiSelectionDropdown
        ariaLabel={t('filters.allSites')}
        value={dropdownValue}
        options={options}
        onChange={handleChange}
        placeholder={t('filters.allSites')}
        renderValue={renderValue}
        popoverMinWidth={240}
      />
    </Styled.FilterWrapper>
  );
}
